import type { CalendarDate } from "../shared/calendar-date";
import type { Food, FoodBasis, FoodMeasure, FoodPortion } from "../foods/food";
import type { NutrientProfile } from "../nutrition/nutrients";
import { scaleNutrients } from "../nutrition/nutrients";
import { nutrientsForPortion, validateFood, validateFoodPortion } from "../foods/food";
import { caloriesFromNutrients } from "../nutrition/energy";
import { catalogMicronutrientsOnly } from "../nutrition/nutrient-normalization";

export const MEAL_NAMES = ["Breakfast", "Lunch", "Dinner", "Snack"] as const;
export type MealName = (typeof MEAL_NAMES)[number];

/** Maps legacy and missing meal values into the four diary sections. */
export function normalizeMealName(meal?: string): MealName {
  if (meal === "Lunch" || meal === "Dinner" || meal === "Snack" || meal === "Snacks") {
    return meal === "Snacks" ? "Snack" : meal;
  }
  return "Breakfast";
}

export interface LoggedFoodSnapshot {
  readonly foodId: string;
  readonly name: string;
  readonly brand?: string;
  readonly barcode?: string;
  readonly basis: FoodBasis;
  readonly measures?: readonly FoodMeasure[];
  readonly portion: FoodPortion;
  readonly nutrients: NutrientProfile;
  readonly calories: number;
}

export interface DiaryEntry {
  readonly id: string;
  readonly date: CalendarDate;
  readonly meal?: string;
  readonly food: LoggedFoodSnapshot;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateDiaryEntryInput {
  readonly id: string;
  readonly date: CalendarDate;
  readonly meal?: string;
  readonly food: Food;
  readonly portion: FoodPortion;
  readonly now: string;
}

export interface UpdateDiaryEntryInput {
  readonly date: CalendarDate;
  readonly meal?: string;
  readonly portion: FoodPortion;
  readonly now: string;
}

/** Copies nutrition into history so later food/provider edits cannot rewrite past intake. */
export function createDiaryEntry(input: CreateDiaryEntryInput): DiaryEntry {
  const food = validateFood(input.food);
  const portion = validateFoodPortion(input.portion, food.basis, food.measures);
  const scaledNutrients = nutrientsForPortion(food, portion);
  const nutrients: NutrientProfile = {
    ...scaledNutrients,
    micronutrients: catalogMicronutrientsOnly(scaledNutrients.micronutrients),
  };
  return {
    id: input.id,
    date: input.date,
    meal: input.meal,
    food: {
      foodId: food.id,
      name: food.name,
      brand: food.brand,
      barcode: food.barcode,
      basis: { ...food.basis },
      ...(food.measures === undefined ? {} : { measures: food.measures.map((measure) => ({ ...measure })) }),
      portion: clonePortion(portion),
      nutrients,
      calories: caloriesFromNutrients(nutrients),
    },
    createdAt: input.now,
    updatedAt: input.now,
  };
}

/** Rescales the stored snapshot so edits never depend on a food or recipe's current definition. */
export function updateDiaryEntry(entry: DiaryEntry, input: UpdateDiaryEntryInput): DiaryEntry {
  const portion = validateFoodPortion(input.portion, entry.food.basis, entry.food.measures);
  const previousPortion = validateFoodPortion(entry.food.portion, entry.food.basis, entry.food.measures);
  const nutrients = scaleNutrients(entry.food.nutrients, portion.amount / previousPortion.amount);
  return {
    ...entry,
    date: input.date,
    meal: input.meal,
    food: {
      ...entry.food,
      basis: { ...entry.food.basis },
      portion: clonePortion(portion),
      nutrients,
      calories: caloriesFromNutrients(nutrients),
    },
    updatedAt: input.now,
  };
}

function clonePortion(portion: FoodPortion): FoodPortion {
  return {
    amount: portion.amount,
    unit: portion.unit,
    ...(portion.display === undefined ? {} : { display: { ...portion.display } }),
  };
}
